"use client";

import { c as _c } from "react/compiler-runtime";
import type { RefObject } from "react";
import { useCallback, useEffect, useRef, useState } from "react";
type UnreadPosition = "hidden" | "above" | "below";
interface UseUnreadDividerVisibilityArgs {
  readonly unreadDividerSerialNo: number | null;
  readonly scrollContainerRef: RefObject<HTMLDivElement | null>;
  readonly onDismissUnread: () => void;
}
export function useUnreadDividerVisibility(t0) {
  const $ = _c(19);
  const {
    unreadDividerSerialNo,
    scrollContainerRef,
    onDismissUnread
  } = t0;
  const [unreadPosition, setUnreadPosition] = useState("hidden");
  const [hasSeenDivider, setHasSeenDivider] = useState(false);
  const [dismissedSerialNo, setDismissedSerialNo] = useState(null);
  const hasSeenDividerRef = useRef(false);
  const wasInViewRef = useRef(null);
  const hasAutoDismissedRef = useRef(false);
  const lastSerialNoRef = useRef(null);
  let t1;
  if ($[0] !== onDismissUnread || $[1] !== unreadDividerSerialNo) {
    t1 = () => {
      if (unreadDividerSerialNo === null) {
        return;
      }
      setDismissedSerialNo(unreadDividerSerialNo);
      onDismissUnread();
    };
    $[0] = onDismissUnread;
    $[1] = unreadDividerSerialNo;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const dismissUnread = t1;
  let t2;
  if ($[3] !== onDismissUnread || $[4] !== scrollContainerRef || $[5] !== unreadDividerSerialNo) {
    t2 = () => {
      if (lastSerialNoRef.current !== unreadDividerSerialNo) {
        lastSerialNoRef.current = unreadDividerSerialNo;
        hasSeenDividerRef.current = false;
        wasInViewRef.current = null;
        hasAutoDismissedRef.current = false;
        setHasSeenDivider(false);
      }
      if (unreadDividerSerialNo === null) {
        setUnreadPosition("hidden");
        wasInViewRef.current = null;
        return;
      }
      const scrollContainer = scrollContainerRef.current;
      if (!scrollContainer) {
        setUnreadPosition("hidden");
        wasInViewRef.current = null;
        return;
      }
      const unreadElement = scrollContainer.querySelector(`#drop-${unreadDividerSerialNo}`);
      if (!unreadElement) {
        setUnreadPosition("above");
        wasInViewRef.current = null;
        return;
      }
      const containerRect = scrollContainer.getBoundingClientRect();
      const elementRect = unreadElement.getBoundingClientRect();
      const isAboveViewport = elementRect.bottom < containerRect.top;
      const isBelowViewport = elementRect.top > containerRect.bottom;
      const isInView = !isAboveViewport && !isBelowViewport;
      const wasInView = wasInViewRef.current === true;
      if (isInView) {
        if (!hasSeenDividerRef.current) {
          hasSeenDividerRef.current = true;
          setHasSeenDivider(true);
        }
        setUnreadPosition("hidden");
      } else {
        if (hasSeenDividerRef.current && wasInView && !hasAutoDismissedRef.current) {
          hasAutoDismissedRef.current = true;
          onDismissUnread();
        }
        setUnreadPosition(isAboveViewport ? "above" : "below");
      }
      wasInViewRef.current = isInView;
    };
    $[3] = onDismissUnread;
    $[4] = scrollContainerRef;
    $[5] = unreadDividerSerialNo;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const checkUnreadVisibility = t2;
  let t3;
  let t4;
  if ($[7] !== checkUnreadVisibility || $[8] !== scrollContainerRef) {
    t3 = () => {
      const scrollContainer_0 = scrollContainerRef.current;
      if (!scrollContainer_0) {
        return;
      }
      const handleScroll = () => {
        checkUnreadVisibility();
      };
      scrollContainer_0.addEventListener("scroll", handleScroll, {
        passive: true
      });
      const observer = new MutationObserver(() => {
        checkUnreadVisibility();
      });
      observer.observe(scrollContainer_0, {
        childList: true,
        subtree: true
      });
      scrollContainer_0.dispatchEvent(new Event("scroll"));
      return () => {
        scrollContainer_0.removeEventListener("scroll", handleScroll);
        observer.disconnect();
      };
    };
    t4 = [checkUnreadVisibility, scrollContainerRef];
    $[7] = checkUnreadVisibility;
    $[8] = scrollContainerRef;
    $[9] = t3;
    $[10] = t4;
  } else {
    t3 = $[9];
    t4 = $[10];
  }
  useEffect(t3, t4);
  const isUserDismissed = unreadDividerSerialNo !== null && dismissedSerialNo === unreadDividerSerialNo;
  const isUnreadVisible = unreadPosition !== "hidden" && unreadDividerSerialNo !== null && !hasSeenDivider && !isUserDismissed;
  let t5;
  let t6;
  if ($[11] !== dismissUnread || $[12] !== isUnreadVisible) {
    t5 = () => {
      if (!isUnreadVisible) {
        return;
      }
      const handleKeyDown = e => {
        if (e.key !== "Escape") {
          return;
        }
        const active = document.activeElement;
        if (active?.closest("[role=\"dialog\"]")) {
          return;
        }
        dismissUnread();
      };
      document.addEventListener("keydown", handleKeyDown);
      return () => document.removeEventListener("keydown", handleKeyDown);
    };
    t6 = [dismissUnread, isUnreadVisible];
    $[11] = dismissUnread;
    $[12] = isUnreadVisible;
    $[13] = t5;
    $[14] = t6;
  } else {
    t5 = $[13];
    t6 = $[14];
  }
  useEffect(t5, t6);
  let t7;
  if ($[15] !== dismissUnread || $[16] !== isUnreadVisible || $[17] !== unreadPosition) {
    t7 = {
      unreadPosition,
      isUnreadVisible,
      dismissUnread
    };
    $[15] = dismissUnread;
    $[16] = isUnreadVisible;
    $[17] = unreadPosition;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  return t7;
}